/** Format invoice fields for view / print / PDF */

const INVALID_DATE_PREFIXES = ["0000-00-00", "0000-00-00 00:00:00"];

export function isValidInvoiceDate(value) {
  const s = String(value || "").trim();
  if (!s) return false;
  return !INVALID_DATE_PREFIXES.some((bad) => s.startsWith(bad));
}

export function formatInvoiceDateTime(value) {
  if (!isValidInvoiceDate(value)) return "—";
  const s = String(value).trim();
  if (s.length >= 10) {
    return s.slice(0, 10) + (s.length > 10 ? ` ${s.slice(11, 19)}` : "");
  }
  return s;
}

export function formatServicePeriod(start, end) {
  const startOk = isValidInvoiceDate(start);
  const endOk = isValidInvoiceDate(end);
  const startLabel = startOk ? String(start).slice(0, 10) : null;
  const endLabel = endOk ? String(end).slice(0, 10) : null;
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  if (endLabel) return `Through ${endLabel}`;
  if (startLabel) return `From ${startLabel}`;
  return "—";
}

export function formatMoneyCents(amountCents) {
  const n = Number(amountCents);
  if (!Number.isFinite(n) || n < 0) return "0.00";
  return (n / 100).toFixed(2);
}

export function formatPaymentMethodLabel(invoice) {
  const last4 = String(invoice?.card_last_4 || "").replace(/\D/g, "").slice(-4);
  if (last4) {
    const brand = String(invoice?.card_brand || "Card").trim();
    const brandLabel =
      brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
    return `${brandLabel} •••• ${last4}`;
  }
  const brand = String(invoice?.card_brand || "").trim();
  if (brand && brand.toLowerCase() !== "null" && brand.toLowerCase() !== "card") {
    return brand;
  }
  const legacy = String(invoice?.payment_method || "").trim();
  if (legacy) {
    const match = legacy.match(/(\d{4})\s*$/);
    if (match) {
      return `Card •••• ${match[1]}`;
    }
    return legacy;
  }
  return "Card on file";
}

const INVOICE_CATEGORY_LABELS = {
  backup: "Backup",
  renewal_backup: "Renewal Backup",
  vault: "Vault",
  vault_renewal: "Vault Renewal",
  restore: "Restore",
};

function isEmptyPeriodDate(value) {
  const s = String(value || "").trim();
  return !s || INVALID_DATE_PREFIXES.some((bad) => s.startsWith(bad));
}

function invoiceCategoryFromRow(invoice) {
  const svc = String(invoice?.service_name || "")
    .trim()
    .toLowerCase();
  if (INVOICE_CATEGORY_LABELS[svc]) {
    return svc;
  }
  if (svc.includes("restore")) {
    return "restore";
  }
  const subId = String(invoice?.subscription_id || "").trim();
  const hasSubscription = subId !== "" && subId.toLowerCase() !== "null";
  if (!hasSubscription && isEmptyPeriodDate(invoice?.subs_current_period_start)) {
    return "restore";
  }
  if (svc.includes("inactive")) {
    return invoice?.is_renewal ? "vault_renewal" : "vault";
  }
  if (hasSubscription) {
    const subType = String(invoice?.subscription_type || "").trim();
    const isRenewal = Boolean(invoice?.is_renewal);
    if (subType === "inactive_orgs_sub") {
      return isRenewal ? "vault_renewal" : "vault";
    }
    return isRenewal ? "renewal_backup" : "backup";
  }
  return "backup";
}

export function lineDescription(invoice) {
  if (!invoice) return "—";
  const precomputed = String(invoice.line_description || "").trim();
  if (precomputed) {
    return precomputed;
  }
  const orgName = String(invoice.org_name || "").trim();
  const category = invoiceCategoryFromRow(invoice);
  const suffix = INVOICE_CATEGORY_LABELS[category] || "Service";
  if (orgName) {
    return `${orgName} - ${suffix}`;
  }
  const legacy = String(invoice.service_name || "").trim();
  if (legacy && !INVOICE_CATEGORY_LABELS[legacy.toLowerCase()]) {
    return legacy;
  }
  return suffix || "—";
}
